import { Box, CircularProgress, Grid, Typography } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { DropzoneArea } from "mui-file-dropzone"
import { useState } from "react"
import chapterService from "services/chapterService"
import { addChapter } from "slices/chapterSlice"
import type { ICreateChapter } from "types/chapterTypes"
import * as Yup from "yup"

export function CreateChapterDialog() {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)

  const dispatch = useAppDispatch()

  const currentTrainingModule = useAppSelector(
    (state) => state.trainingmodule.currentTrainingModule,
  )

  const initialValues: ICreateChapter = {
    trainingModuleId: "",
    name: "",
    description: "",
    imageFile: undefined,
    id: "",
  }

  const createChapterValidationScheme = Yup.object({
    name: Yup.string()
      .required("Please enter chapter name")
      .max(64, "Maximum name length is 64 characters"),
    description: Yup.string()
      .required("Please enter chapter description")
      .max(256, "Maximum description length is 256 characters"),
    imageFile: Yup.mixed().required("Please enter image file"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: createChapterValidationScheme,
    onSubmit: (values) => {
      if (currentTrainingModule === undefined) {
        return
      }

      setIsLoading(true)

      const newChapter: ICreateChapter = {
        trainingModuleId: currentTrainingModule.id,
        name: values.name,
        description: values.description,
        imageFile: values.imageFile,
        id: values.id,
      }

      chapterService
        .createChapter(newChapter)
        .then((resp) => {
          dispatch(addChapter(resp))
          setIsLoading(false)
          handleClose()
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <Button variant="contained" onClick={handleClickOpen}>
        create Chapter
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Create Chapter</DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  required
                  autoFocus
                  margin="dense"
                  id="name"
                  label="Name"
                  type="name"
                  fullWidth
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  id="id"
                  label="(Optional) Id"
                  type="name"
                  fullWidth
                  variant="outlined"
                  value={formik.values.id}
                  onChange={formik.handleChange}
                  error={formik.touched.id && Boolean(formik.errors.id)}
                  helperText={formik.touched.id && formik.errors.id}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  margin="dense"
                  id="description"
                  label="Description"
                  type="description"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                />
              </Grid>
              <Grid item xs={12}>
                <DropzoneArea
                  onChange={(files) => {
                    formik.setFieldValue("imageFile", files[0])
                  }}
                  filesLimit={1}
                  fileObjects={formik.values.imageFile}
                  acceptedFiles={["image/jpeg", "image/png", "image/bmp"]}
                />
                <Typography
                  marginTop={1}
                  component="h6"
                  variant="body2"
                  color="error"
                  hidden={formik.touched.imageFile === undefined}
                >
                  Please add image file
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Create
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
